import { Injectable } from "@angular/core";
import _ from "lodash";
import * as moment from "moment";
import { Subject } from "rxjs";
import { StoreService } from "./store.service";

const DateFormatTable = [
  // Y-M-D
  { value: 0, ymd: "YYYY-MM-DD", ym: "YYYY-MM", md: "MM-DD" },
  { value: 1, ymd: "YYYY/MM/DD", ym: "YYYY/MM", md: "MM/DD" },
  { value: 10, ymd: "YYYY.MM.DD", ym: "YYYY.MM", md: "MM.DD" },
  { value: 11, ymd: "YYYY MMM DD", ym: "YYYY MMM", md: "MMM DD" },
  // Y-D-M
  { value: 12, ymd: "YYYY-DD-MM", ym: "YYYY MMM", md: "DD-MM" },
  { value: 13, ymd: "YYYY/DD/MM", ym: "YYYY MMM", md: "DD/MM" },
  { value: 14, ymd: "YYYY.DD.MM", ym: "YYYY MMM", md: "DD.MM" },
  { value: 15, ymd: "YYYY DD MMM", ym: "YYYY MMM", md: "DD MMM" },
  // M-D-Y
  { value: 2, ymd: "MM-DD-YYYY", ym: "MMM YYYY", md: "MM-DD" },
  { value: 3, ymd: "MM/DD/YYYY", ym: "MMM YYYY", md: "MM/DD" },
  { value: 16, ymd: "MM.DD.YYYY", ym: "MMM YYYY", md: "MM.DD" },
  { value: 4, ymd: "MMM DD YYYY", ym: "MMM YYYY", md: "MMM DD" },
  // D-M-Y
  { value: 6, ymd: "DD-MM-YYYY", ym: "MMM YYYY", md: "DD-MM" },
  { value: 7, ymd: "DD/MM/YYYY", ym: "MMM YYYY", md: "DD/MM" },
  { value: 17, ymd: "DD.MM.YYYY", ym: "MMM YYYY", md: "DD.MM" },
  { value: 8, ymd: "DD MMM YYYY", ym: "MMM YYYY", md: "DD MMM" },
];

const TimeFormatTable = [
  { value: 0, hms: "HH:mm:ss", hm: "HH:mm", ms: "mm:ss", h: "HH" },
  { value: 1, hms: "hh:mm:ssa", hm: "hh:mma", ms: "mm:ss", h: "hha" },
];

enum IntlTimeFormat {
  TwentyFour = 0, // 24小时制
  Twelve = 1, // 12小时制
}

@Injectable({
  providedIn: "root",
})
export class DateService {
  private readonly dateFormatKey = "hostex-i18n-dateformat";
  private readonly timeFormatKey = "hostex-i18n-timeformat";

  private dateFormat: number = 0;

  /** 时间的格式化 */
  private timeFormat: number = 0;

  dateFormatChange$ = new Subject();
  timeFormatChange$ = new Subject();

  get use12hr(): boolean {
    return this.timeFormat === IntlTimeFormat.Twelve;
  }

  constructor(private storeService: StoreService) {}

  /**
   * @public
   * @params f: Array<string> || string
   * @example
   * ///   format(['YMD', 'hms']) 根据系统设置格式时间 Y年 M月 D日  h时 m分 s秒
   * ///   format(['YMD']) 只显示日期，不显示时间
   * ///   format(['MD']) 只展示月和日
   * ///   format(['YM']) 只展示年和月
   * ///   format(['YM', 'hm']) 展示年月与时分
   *
   * ///   format('YYYY-MM-DD')   自定义格式时间
   */

  format(value, f): string {
    const mo = moment(value);
    mo.locale("en");

    if (_.isArray(f) || !f) {
      const format = this.getMomentFormat(f);
      return mo.format(format);
    } else {
      return mo.format(f);
    }
  }

  setDateFormat(format: number) {
    console.log("🚀 ~ DateService ~ setDateFormat ~ format:", format);

    this.dateFormat = format;
    this.setDateFormatToLocal(format);
    this.dateFormatChange$.next(null);
  }

  setTimeFormat(format: number) {
    console.log("🚀 ~ DateService ~ setTimeFormat ~ format:", format);
    this.timeFormat = format;
    this.setTimeFormatToLocal(format);
    this.timeFormatChange$.next(null);
  }

  getTimeFormat(): number {
    if (_.isNil(this.timeFormat)) {
      return this.getTimeFormatFromLocal();
    }
    return this.timeFormat;
    // return this.timeFormat || this.getTimeFormatFromLocal();
  }

  getDateFormat(): number {
    if (_.isNil(this.dateFormat)) {
      this.getDateFormatFromLocal();
    }
    return this.dateFormat;
    // return this.dateFormat || this.getDateFormatFromLocal();
  }

  private getDateFormatFromLocal(): number {
    return +this.storeService.get(this.dateFormatKey, 2);
  }
  private getTimeFormatFromLocal(): number {
    return +this.storeService.get(this.timeFormatKey, 2);
  }

  private setDateFormatToLocal(format: number) {
    this.storeService.set(this.dateFormatKey, format, 2);
  }
  private setTimeFormatToLocal(format: number) {
    this.storeService.set(this.timeFormatKey, format, 2);
  }

  /**
   * @private
   * 获取moment库需要的格式化字符串
   * EXAMPLE: YYYY-MM-DD
   */
  private getMomentFormat(formats: Array<string> | null): string {
    if (_.isEmpty(formats)) {
      return `${this.getDateFormatByField("YMD")} ${this.getTimeFormatByField("hms")}`;
    }

    const [df, tf] = formats;
    if (!tf) {
      return this.getDateFormatByField(df);
    }
    if (!df) {
      return this.getTimeFormatByField(tf);
    }

    return `${this.getDateFormatByField(df)} ${this.getTimeFormatByField(tf)}`;
  }

  /**
   * @public
   * 获取内部管道需要的格式化时间字符串
   * 与moment的字符串略有不同，Y -> y, D -> d
   * 一般为ng-zorro-antd时间组件的显示需要
   */
  getAngularFormat(formats: Array<string> | null) {
    return this.getMomentFormat(formats).replace(/Y/g, "y").replace(/D/g, "d");
  }

  getDateFormatByField(field: string) {
    // const value = this.international.getDateFormat() || 0;
    const value = this.getDateFormat() || 0;
    const item = _.find(DateFormatTable, { value });

    return item && item[_.toLower(field)];
  }

  getTimeFormatByField(field: string) {
    // const value = this.international.getTimeFormat() || 0;
    const value = this.getTimeFormat() || 0;
    const item = _.find(TimeFormatTable, { value });
    return item && item[_.toLower(field)];
  }

  clearLocalKeys() {
    this.storeService.remove(this.dateFormatKey, 2);
    this.storeService.remove(this.timeFormatKey, 2);
  }
}
